/**
 * Copyright 2026 Perform Content Services Limited. Confidential and Proprietary. All Rights Reserved.
 */

import Disposable from '../../lang/Disposable';
import DisposableList from '../../lang/DisposableList';
import {ILogger} from '../../logger/LoggerInterface';
import LoggerFactory from '../../logger/LoggerFactory';
import {ChunkedStreaming} from './ChunkedStreaming';
import {ChunkedStreamingOptions, ChunkedStreamingStatistics} from './StreamTypes';
import {IShakaPlayer, ShakaOptions} from './IShakaPlayer';
import {NetworkStates} from './NetworkStates';

export default class ShakaPlayer {
  private readonly _logger: ILogger = LoggerFactory.getLogger('ShakaPlayer');
  private readonly _videoElement: HTMLVideoElement;
  private readonly _kind: string;
  private readonly _streamId: string;
  private readonly _manifestURI: string;
  private readonly _options: ChunkedStreamingOptions;
  private readonly _handleStreamFailure: Function;
  private readonly _disposables: DisposableList;
  private readonly _monitoredStatistics: {
    estimatedBandwidth: number | undefined;
    playTime: number | undefined;
  };
  private _player: IShakaPlayer | null;

  constructor(videoElement: HTMLVideoElement, kind: string, streamId: string, manifestURI: string, options: ChunkedStreamingOptions, handleStreamFailure: Function) {
    this._videoElement = videoElement;
    this._kind = kind;
    this._streamId = streamId;
    this._manifestURI = encodeURI(manifestURI).replace(/[#]/g, '%23');
    this._options = options;
    this._handleStreamFailure = handleStreamFailure;
    this._disposables = new DisposableList();
    this._monitoredStatistics = {
      estimatedBandwidth: 0,
      playTime: 0
    };
    this._player = null;
  }

  start(): void {
    if (ChunkedStreaming.shaka) {
      const playerOptions: ShakaOptions = {
        abr: {defaultBandwidthEstimate: 2000000},
        manifest: {retryParameters: {timeout: 10000}},
        streaming: {
          liveSyncPanicThreshold: 1,
          bufferingGoal: 60,
          bufferBehind: 30,
          retryParameters: {
            timeout: 10000,
            maxAttempts: 10,
            backoffFactor: 1.1
          }
        }
      };

      this._player = new ChunkedStreaming.shaka.Player(this._videoElement);
      this.loadPlayer(playerOptions);

      return;
    }
  }

  getStats(): ChunkedStreamingStatistics {
    if (!this._player) {
      return {
        width: 0,
        height: 0,
        currentTime: 0,
        lag: 0,
        networkState: NetworkStates.NetworkNoSource,
        dataBuffered: 0.0
      };
    }

    const statistics = {
      ...this._player.getStats(),
      currentTime: 0,
      lag: 0
    };
    const currentTime = this._videoElement['currentTime'] || 0;
    const trueCurrentTime = (Date.now() - this._options.originStartTime) / 1000;
    const lag = Math.max(0.0, trueCurrentTime - currentTime);

    if (this._videoElement) {
      statistics.currentTime = currentTime;
      statistics.lag = lag;

      if (this._videoElement.buffered && this._videoElement.buffered.length) {
        statistics.dataBuffered = this._videoElement.buffered.end(0);
      }
    }

    if (statistics.estimatedBandwidth) {
      if (statistics.estimatedBandwidth && statistics.estimatedBandwidth > 0 && (this._monitoredStatistics.estimatedBandwidth && this._monitoredStatistics.estimatedBandwidth < statistics.estimatedBandwidth)) {
        statistics.networkState = NetworkStates.NetworkLoading;
      } else if (statistics.playTime && statistics.playTime > 0 && (this._monitoredStatistics.playTime && this._monitoredStatistics.playTime < statistics.playTime)) {
        statistics.networkState = NetworkStates.NetworkIdle;
      } else if (statistics.video) {
        statistics.networkState = NetworkStates.NetworkEmpty;
      } else {
        statistics.networkState = NetworkStates.NetworkNoSource;
      }
    }

    this._monitoredStatistics.estimatedBandwidth = statistics.estimatedBandwidth;
    this._monitoredStatistics.playTime = statistics.playTime;

    return statistics;
  }

  dispose(): void {
    this._disposables.dispose();

    if (this._player) {
      this._player.unload();
      this._player.destroy();
    }

    this._player = null;
  }

  private loadPlayer(playerConfig: ShakaOptions): void {
    this._player?.configure(playerConfig);

    const errorHandler = () => this._handleStreamFailure();

    this._player?.addEventListener('error', errorHandler);

    this._disposables.add(new Disposable(() => this._player?.removeEventListener('error', errorHandler)));

    const ignored = this._player?.load(this._manifestURI).then(() => {
      this._logger.info('[%s] DASH live stream has been loaded', this._streamId);

      if (this._videoElement.play) {
        this._videoElement.play();
      }
    }).catch(e => {
      this._logger.error('[%s] Error while loading DASH live stream [%s]', this._streamId, e.code, e);
    });
  }
}